import { FC } from 'react';
import {
  AssessmentType,
  CbiSubscale,
  LatestCompletedProgramAssessmentsQuery,
} from '../../../../../generated/graphql';
import ContentPreview from '../../../../components/ContentPreview';
import {
  AssessmentCompletionDataTypename,
  formatCompletionCount,
} from '../../../../lib/assessments';

import { getTimeStamp } from '../../../../lib/time';
import LatestAssessmentCard from './LatestAssessmentCard';
import { ContentType } from '../../../../lib/followUp';

type LatestCompletedProgramAssessment =
  LatestCompletedProgramAssessmentsQuery['latestCompletedProgramAssessments'][number];

type LatestAssessmentCardsProps = {
  latestCompletedProgramAssessments: LatestCompletedProgramAssessmentsQuery['latestCompletedProgramAssessments'];
  setSelectedAssessment: React.Dispatch<
    React.SetStateAction<
      LatestCompletedProgramAssessment['assessment'] | undefined
    >
  >;
  setIsAssessmentsCompletedSlideoverOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  /** When true, section title shows the content-type icon; hide it on each card */
  hideThumbnail?: boolean;
  /** Patient first name for "Completed by {name}" on client-completed forms */
  patientFirstName?: string | null;
};

const LatestAssessmentCards: FC<LatestAssessmentCardsProps> = ({
  latestCompletedProgramAssessments,
  setSelectedAssessment,
  setIsAssessmentsCompletedSlideoverOpen,
  hideThumbnail = false,
  patientFirstName,
}) => {
  const openHistorySlideover = (
    latestCompletedProgramAssessment: LatestCompletedProgramAssessment,
  ) => {
    setSelectedAssessment(latestCompletedProgramAssessment.assessment);
    setIsAssessmentsCompletedSlideoverOpen(true);
  };

  return (
    <div className="mt-6 flex flex-wrap gap-7">
      {latestCompletedProgramAssessments?.map(
        (latestCompletedProgramAssessment) => {
          const {
            id,
            completedAt,
            completionCount,
            assessment: { type, shortName, name, increasingScoreTowardsHealth },
          } = latestCompletedProgramAssessment;
          const completionDataTypename =
            latestCompletedProgramAssessment?.completionData?.__typename;
          const completedDate = getTimeStamp(completedAt, false);

          if (
            completionDataTypename ===
            AssessmentCompletionDataTypename.MultipleChoiceSumScoredAssessmentCompletionData
          ) {
            const {
              completionData: { score, color, categorization },
            } = latestCompletedProgramAssessment;

            return (
              <LatestAssessmentCard
                key={id}
                shortName={shortName}
                color={color ?? 'neutral'}
                increasingScoreTowardsHealth={
                  increasingScoreTowardsHealth || false
                }
                completedDate={completedDate}
                completionCount={completionCount}
                primaryScore={{
                  score,
                  categorization,
                }}
                onClick={() => {
                  openHistorySlideover(latestCompletedProgramAssessment);
                }}
                hasPercentage={type === AssessmentType.Meq30}
              />
            );
          }

          if (
            completionDataTypename ===
              AssessmentCompletionDataTypename.HADSAssessmentCompletionData ||
            completionDataTypename ===
              AssessmentCompletionDataTypename.ASRS19AssessmentCompletionData
          ) {
            const {
              completionData: { subscores },
            } = latestCompletedProgramAssessment;

            return (
              <LatestAssessmentCard
                key={id}
                shortName={shortName}
                completedDate={completedDate}
                completionCount={completionCount}
                color={'neutral'}
                increasingScoreTowardsHealth={
                  increasingScoreTowardsHealth || false
                }
                primaryScore={{
                  score: subscores[0].score,
                  categorization: subscores[0].categorization,
                }}
                secondaryScore={{
                  score: subscores[1].score,
                  categorization: subscores[1].categorization,
                }}
                onClick={() => {
                  openHistorySlideover(latestCompletedProgramAssessment);
                }}
              />
            );
          }

          if (
            completionDataTypename ===
            AssessmentCompletionDataTypename.CBIAssessmentCompletionData
          ) {
            const {
              completionData: { subscores },
            } = latestCompletedProgramAssessment;

            const cbiCardLabels: Record<string, string> = {
              [CbiSubscale.PersonalBurnout]: 'Personal',
              [CbiSubscale.WorkRelatedBurnout]: 'Work',
              [CbiSubscale.ClientRelatedBurnout]: 'Client',
            };

            return (
              <LatestAssessmentCard
                key={id}
                shortName={shortName}
                completedDate={completedDate}
                completionCount={completionCount}
                color={'neutral'}
                compactScores={subscores.map((subscore) => ({
                  label: cbiCardLabels[subscore.subscale] ?? 'Score',
                  score: subscore.score,
                }))}
                onClick={() => {
                  openHistorySlideover(latestCompletedProgramAssessment);
                }}
              />
            );
          }

          if (
            completionDataTypename ===
            AssessmentCompletionDataTypename.CustomAssessmentCompletionData
          ) {
            const isPractitionerForm =
              latestCompletedProgramAssessment.assessment
                ?.completableByProviderOnly === true;
            const signedSignatureUser =
              latestCompletedProgramAssessment.programAssessmentSignatureUsers?.find(
                (u) => u.signedAt,
              );
            const signatureProvider = signedSignatureUser?.providerUser;
            const completedByProviderUser =
              latestCompletedProgramAssessment.completedByProviderUser;
            const completedByProviderName =
              signatureProvider?.name ??
              (latestCompletedProgramAssessment.completedByType === 'provider'
                ? completedByProviderUser?.name
                : latestCompletedProgramAssessment.completedByType ===
                  'external_party'
                ? latestCompletedProgramAssessment.completedByExternalName ??
                  'External'
                : null);
            const completedByProvider = completedByProviderName
              ? { name: completedByProviderName }
              : undefined;
            const completedByClient =
              isPractitionerForm &&
              latestCompletedProgramAssessment.completedByType === 'patient';
            const signedByNames =
              latestCompletedProgramAssessment.programAssessmentSignatureUsers
                ?.filter((u) => u.signedAt)
                .map((u) => u.providerUser?.name ?? u.patientName ?? null)
                .filter((n): n is string => Boolean(n)) ?? [];
            const completionCountLabel = formatCompletionCount(completionCount);

            return (
              <ContentPreview
                key={`completedFormContentPreview_${id}`}
                contentType={ContentType.Form}
                formVariant={isPractitionerForm ? 'practitioner' : 'client'}
                className="max-w-[284px] !p-3"
                title={name}
                completedByProvider={completedByProvider}
                completedDate={
                  completionCountLabel
                    ? `${completedDate} · ${completionCountLabel}`
                    : completedDate
                }
                completedByLabel={
                  isPractitionerForm && completedByClient
                    ? 'Client'
                    : !isPractitionerForm
                    ? patientFirstName ?? 'Client'
                    : undefined
                }
                hideThumbnail={hideThumbnail}
                signedByNames={
                  signedByNames.length > 0 ? signedByNames : undefined
                }
                onClick={() => {
                  openHistorySlideover(latestCompletedProgramAssessment);
                }}
              />
            );
          }

          return null;
        },
      )}
    </div>
  );
};

export default LatestAssessmentCards;
